import React, { FC, useEffect, useMemo } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Setting,
  SettingApplication,
  SettingType,
} from "@/domain/settings.interface";
import { SettingPayload } from "@/services/settings-catalog.service";

const APPLICATIONS: SettingApplication[] = ["mobile", "backoffice", "platform"];
const TYPES: SettingType[] = ["string", "number", "boolean", "object", "array"];

type SettingFormData = {
  application: SettingApplication;
  module: string;
  section: string;
  name: string;
  description: string;
  type: SettingType;
  // For non-object/array types the raw scalar is stored directly.
  // For object/array types the form stores a JSON string that we parse on submit.
  defaultString: string;
  defaultNumber: number | string;
  defaultBoolean: boolean;
  defaultJson: string;
  active: boolean;
};

const DEFAULT_VALUES: SettingFormData = {
  application: "mobile",
  module: "",
  section: "",
  name: "",
  description: "",
  type: "string",
  defaultString: "",
  defaultNumber: "",
  defaultBoolean: false,
  defaultJson: "",
  active: true,
};

const buildSchema = () =>
  yup.object().shape({
    application: yup
      .mixed<SettingApplication>()
      .oneOf(APPLICATIONS)
      .required("Application is required"),
    module: yup.string().trim().required("Module is required"),
    section: yup.string().trim().required("Section is required"),
    name: yup.string().trim().required("Name is required"),
    description: yup.string().trim().required("Description is required"),
    type: yup.mixed<SettingType>().oneOf(TYPES).required("Type is required"),
    defaultString: yup.string().optional(),
    defaultNumber: yup.mixed().optional(),
    defaultBoolean: yup.boolean().required(),
    defaultJson: yup.string().optional(),
    active: yup.boolean().required(),
  });

type SettingModalProps = {
  open: boolean;
  onClose: () => void;
  setting?: Setting;
  onSave: (data: SettingPayload) => void;
  isPending: boolean;
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? "", null, 2);
  } catch {
    return "";
  }
};

const SettingModal: FC<SettingModalProps> = ({
  open,
  onClose,
  setting,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(setting);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SettingFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const watchedType = watch("type");

  useEffect(() => {
    if (!open) return;
    if (setting) {
      const base: SettingFormData = { ...DEFAULT_VALUES };
      base.application = setting.application;
      base.module = setting.module || "";
      base.section = setting.section || "";
      base.name = setting.name || "";
      base.description = setting.description || "";
      base.type = setting.type;
      base.active = !!setting.active;

      const defVal = setting.default;
      switch (setting.type) {
        case "string":
          base.defaultString = typeof defVal === "string" ? defVal : "";
          break;
        case "number":
          base.defaultNumber =
            typeof defVal === "number"
              ? defVal
              : defVal === null || defVal === undefined
                ? ""
                : String(defVal);
          break;
        case "boolean":
          base.defaultBoolean = !!defVal;
          break;
        case "object":
        case "array":
          base.defaultJson = safeStringify(defVal);
          break;
      }
      reset(base);
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [setting, open, reset]);

  const onSubmit = (data: SettingFormData) => {
    // Build the `default` value out of the appropriate smart field,
    // parsing JSON for object/array types and surfacing a typed error.
    let defaultValue: unknown = null;
    clearErrors("defaultJson");

    switch (data.type) {
      case "string":
        defaultValue = data.defaultString ?? "";
        break;
      case "number": {
        const raw = data.defaultNumber;
        if (raw === "" || raw === null || raw === undefined) {
          defaultValue = null;
        } else {
          const n = typeof raw === "number" ? raw : Number(raw);
          if (isNaN(n)) {
            defaultValue = null;
          } else {
            defaultValue = n;
          }
        }
        break;
      }
      case "boolean":
        defaultValue = !!data.defaultBoolean;
        break;
      case "object":
      case "array": {
        const raw = (data.defaultJson || "").trim();
        if (!raw) {
          defaultValue = data.type === "array" ? [] : {};
          break;
        }
        try {
          const parsed = JSON.parse(raw);
          if (data.type === "array" && !Array.isArray(parsed)) {
            setError("defaultJson", {
              type: "manual",
              message: "Value must be a JSON array",
            });
            return;
          }
          if (
            data.type === "object" &&
            (Array.isArray(parsed) ||
              parsed === null ||
              typeof parsed !== "object")
          ) {
            setError("defaultJson", {
              type: "manual",
              message: "Value must be a JSON object",
            });
            return;
          }
          defaultValue = parsed;
        } catch {
          setError("defaultJson", {
            type: "manual",
            message: "Invalid JSON",
          });
          return;
        }
        break;
      }
    }

    const payload: SettingPayload = {
      product: "pret",
      application: data.application,
      module: data.module.trim(),
      section: data.section.trim(),
      name: data.name.trim(),
      description: data.description.trim(),
      type: data.type,
      default: defaultValue,
      active: data.active,
    };
    onSave(payload);
  };

  const defaultField = useMemo(() => {
    switch (watchedType) {
      case "string":
        return (
          <Controller
            name="defaultString"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                label="Default value"
                fullWidth
                disabled={isPending}
              />
            )}
          />
        );
      case "number":
        return (
          <Controller
            name="defaultNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                type="number"
                label="Default value"
                fullWidth
                disabled={isPending}
              />
            )}
          />
        );
      case "boolean":
        return (
          <Controller
            name="defaultBoolean"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                    disabled={isPending}
                  />
                }
                label={`Default value: ${field.value ? "true" : "false"}`}
              />
            )}
          />
        );
      case "object":
      case "array":
        return (
          <Controller
            name="defaultJson"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                label={`Default value (JSON ${watchedType})`}
                placeholder={watchedType === "array" ? "[]" : "{\n  \n}"}
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                disabled={isPending}
                error={!!errors.defaultJson}
                helperText={
                  errors.defaultJson?.message ||
                  `Enter a valid JSON ${watchedType}.`
                }
              />
            )}
          />
        );
      default:
        return null;
    }
  }, [watchedType, control, errors.defaultJson, isPending]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Setting" : "Create Setting"}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 760,
        maxWidth: 720,
      }}
      content={
        <Box sx={{ width: "100%", padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="application"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Application"
                    fullWidth
                    required
                    disabled={isPending}
                    error={!!errors.application}
                    helperText={errors.application?.message || ""}
                  >
                    {APPLICATIONS.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Type"
                    fullWidth
                    required
                    disabled={isPending}
                    error={!!errors.type}
                    helperText={errors.type?.message || ""}
                  >
                    {TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="module"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Module"
                    fullWidth
                    required
                    disabled={isPending}
                    error={!!errors.module}
                    helperText={errors.module?.message || ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="section"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Section"
                    fullWidth
                    required
                    disabled={isPending}
                    error={!!errors.section}
                    helperText={errors.section?.message || ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Name"
                    fullWidth
                    required
                    disabled={isPending}
                    error={!!errors.name}
                    helperText={errors.name?.message || ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Description"
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    maxRows={5}
                    disabled={isPending}
                    error={!!errors.description}
                    helperText={errors.description?.message || ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              {defaultField}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(_e, checked) => field.onChange(checked)}
                        disabled={isPending}
                      />
                    }
                    label={field.value ? "Active" : "Inactive"}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default SettingModal;
